import type { JSX } from 'react';
import { useEffect, useRef } from 'react';
import { PRACTICE_NAME, PRACTICE_URL } from '@fatigue/shared';
import { Logo } from './Logo';

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
}

/**
 * Attribution, reachable from the top bar.
 *
 * A native <dialog> rather than a hand-rolled overlay: it gets focus trapping,
 * Escape-to-close and inertness of the page behind it from the platform, which
 * is a lot of accessibility to reimplement badly.
 */
export function AboutDialog({ open, onClose }: Props): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog ref={ref} className="about" onClose={onClose} onCancel={onClose}>
      <div className="about__head">
        <Logo size={40} />
        <div>
          <h2 className="about__name">{PRACTICE_NAME}</h2>
          <a className="about__link" href={PRACTICE_URL} target="_blank" rel="noreferrer noopener">
            midwinterphysiotherapy.co.uk
          </a>
        </div>
      </div>

      <p className="about__body">
        The Fatigue App is a record, not an assessment. Scores are entered by patients and reviewed by a
        physiotherapist in session &mdash; the app does not interpret them, flag them or alert
        anyone.
      </p>

      <p className="about__meta">Clinician view &middot; version 0.1.0</p>

      <form method="dialog">
        <button type="submit" className="about__close">
          Close
        </button>
      </form>
    </dialog>
  );
}
